import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth.store'

type Child = { id: string; firstName: string }

const NUMPAD = ['1','2','3','4','5','6','7','8','9','','0','⌫'] as const

export default function PinPage() {
  const [children,      setChildren]      = useState<Child[]>([])
  const [childId,       setChildId]       = useState('')
  const [pin,           setPin]           = useState('')
  const [loading,       setLoading]       = useState(false)
  const [error,         setError]         = useState('')
  const [loadingList,   setLoadingList]   = useState(true)
  const [listError,     setListError]     = useState(false)

  const { pinLogin } = useAuthStore()
  const navigate     = useNavigate()

  function fetchStudents() {
    setLoadingList(true)
    setListError(false)
    api.get('/auth/students')
      .then((r) => { setChildren(r.data.data as Child[]); setLoadingList(false) })
      .catch(() => { setListError(true); setLoadingList(false) })
  }

  useEffect(() => { fetchStudents() }, [])

  const handleKey = (key: string) => {
    if (key === '⌫') {
      setPin((p) => p.slice(0, -1))
    } else if (pin.length < 6) {
      const next = pin + key
      setPin(next)
      if (next.length >= 4 && childId) submitPin(childId, next)
    }
  }

  const submitPin = async (id: string, code: string) => {
    if (code.length < 4 || !id) return
    setLoading(true)
    setError('')
    try {
      await pinLogin(id, code)
      navigate('/dashboard', { replace: true })
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message
      setError(msg ?? 'Incorrect PIN. Try again.')
      setPin('')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">

        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-2xl overflow-hidden mx-auto mb-4 shadow-md bg-[#F9F7F2]">
            <img src="/sprouthill-icon.svg" alt="Sprout Hill School" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">Sprout Hill School</h1>
          <p className="text-gray-500 text-sm mt-1">Student Sign In</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">

          {/* Child selector */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Who are you?</label>
            {loadingList ? (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-[#1B6B3A] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : listError ? (
              <div className="text-center py-3">
                <p className="text-sm text-red-600 mb-2">Couldn't load students.</p>
                <button
                  onClick={fetchStudents}
                  className="text-sm font-medium text-[#1B6B3A] hover:underline"
                >
                  Try again
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {children.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => { setChildId(c.id); setPin(''); setError('') }}
                    className={`py-3 rounded-xl text-sm font-semibold border-2 transition-all ${
                      childId === c.id
                        ? 'bg-[#1B6B3A] border-[#1B6B3A] text-white'
                        : 'bg-white border-gray-200 text-gray-700 hover:border-[#1B6B3A]'
                    }`}
                  >
                    {c.firstName}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* PIN dots */}
          <div className="flex justify-center gap-3 mb-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={`w-4 h-4 rounded-full border-2 transition-all ${
                  i < pin.length
                    ? 'bg-[#1B6B3A] border-[#1B6B3A]'
                    : 'bg-white border-gray-300'
                }`}
              />
            ))}
          </div>

          {error && (
            <p className="text-center text-red-600 text-sm mb-3">{error}</p>
          )}

          {/* Numpad */}
          <div className="grid grid-cols-3 gap-2">
            {NUMPAD.map((key, i) => (
              <button
                key={i}
                onClick={() => key && handleKey(key)}
                disabled={!key || !childId || loading}
                className={`py-4 rounded-xl text-xl font-semibold transition-all ${
                  key
                    ? 'bg-gray-50 hover:bg-[#1B6B3A] hover:text-white text-[#2C3E50] border border-gray-200 active:scale-95'
                    : 'invisible'
                } disabled:opacity-40`}
              >
                {loading && key === '0' ? '…' : key}
              </button>
            ))}
          </div>
        </div>

        <div className="text-center mt-4">
          <Link to="/login" className="text-sm text-gray-500 hover:text-[#1B6B3A]">
            ← Staff sign in
          </Link>
        </div>
      </div>
    </div>
  )
}

